"use client";

import { memo, useMemo, useCallback } from "react";
import { motion } from "framer-motion";
import equal from "fast-deep-equal";
import { Badge } from "@/components/ui/badge";
import { SourceDisplay } from "../SourceDisplay";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faRedditAlien, faXTwitter } from "@fortawesome/free-brands-svg-icons";
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  Search,
  Newspaper,
} from "lucide-react";
import { cn } from "@/lib/utils";

interface Source {
  text: string;
  author: string;
  score: number | null;
  url: string;
  post_title?: string;
  created_at: string;
  source_type: string;
  originalIndex?: number;
  [key: string]: any;
}

interface SourcesTabProps {
  sourcesWithOriginalIndex: Source[];
  highlightedSourceIndex: number | null;
  sortOrder: "newest" | "oldest";
  setSortOrder: (order: "newest" | "oldest") => void;
  selectedFilters: string[];
  toggleFilter: (filter: string) => void;
  isFilterOpen: boolean;
  setIsFilterOpen: (open: boolean) => void;
  contentView: "preview" | "expanded";
  setContentView: (view: "preview" | "expanded") => void;
  isContentOverflowing: boolean;
  contentRefCallback: (node: HTMLDivElement | null) => void;
  sourcesContainerRef: React.RefObject<HTMLDivElement>;
  filterRef: React.RefObject<HTMLDivElement>;
}

function SourcesTabComponent({
  sourcesWithOriginalIndex,
  highlightedSourceIndex,
  sortOrder,
  setSortOrder,
  selectedFilters,
  toggleFilter,
  isFilterOpen,
  setIsFilterOpen,
  contentView,
  setContentView,
  isContentOverflowing,
  contentRefCallback,
  sourcesContainerRef,
  filterRef,
}: SourcesTabProps) {
  // Memoize filtered and sorted sources
  const filteredSources = useMemo(() => {
    if (!sourcesWithOriginalIndex) return [];

    // Filter sources
    const filtered = sourcesWithOriginalIndex.filter((source) =>
      selectedFilters.includes(source.source_type)
    );

    // Sort sources
    return filtered.sort((a, b) => {
      const dateA = new Date(a.created_at).getTime();
      const dateB = new Date(b.created_at).getTime();
      return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, [sourcesWithOriginalIndex, selectedFilters, sortOrder]);

  // Memoize source counts
  const sourceCounts = useMemo(() => {
    const counts: Record<string, number> = {
      twitter: 0,
      reddit: 0,
      news: 0,
    };

    if (sourcesWithOriginalIndex) {
      sourcesWithOriginalIndex.forEach((source) => {
        counts[source.source_type] = (counts[source.source_type] || 0) + 1;
      });
    }

    return counts;
  }, [sourcesWithOriginalIndex]);

  return (
    <motion.div
      key="sources-code"
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -8 }}
      transition={{ duration: 0.2, ease: "easeOut" }}
      className="rounded-lg w-full max-w-full min-w-0 overflow-hidden"
    >
      {/* Sort and Filter Controls */}
      <div className="flex items-center justify-end gap-2 px-3 pb-1">
        {/* Date Sort Button */}
        <Badge
          variant="outline"
          onClick={() =>
            setSortOrder(sortOrder === "newest" ? "oldest" : "newest")
          }
          className="text-[10px] cursor-pointer transition-colors border border-border px-1.5 py-0.5 text-white bg-black hover:bg-muted"
          title={`Sort by ${
            sortOrder === "newest" ? "oldest" : "newest"
          } first`}
        >
          <Calendar className="w-3 h-3 mr-1" />
          <ChevronDown
            className={`w-3 h-3 transition-transform ${
              sortOrder === "newest" ? "rotate-0" : "rotate-180"
            }`}
          />
        </Badge>

        {/* Filter Dropdown */}
        <div className="relative" ref={filterRef}>
          <Badge
            variant="outline"
            onClick={() => setIsFilterOpen(!isFilterOpen)}
            className="text-[10px] cursor-pointer transition-colors border border-border px-1.5 py-0.5 text-white bg-black hover:bg-muted"
            title="Filter by source type"
          >
            <Search className="w-3 h-3" />
          </Badge>

          {isFilterOpen && (
            <div className="absolute right-0 top-full mt-1 bg-black border border-border rounded-md shadow-lg z-50 min-w-[140px]">
              {Object.entries(sourceCounts).map(
                ([sourceType, count]) => (
                  <label
                    key={sourceType}
                    className="filter-dropdown-item flex items-center gap-2 px-3 py-2 text-[11px] text-white cursor-pointer hover:bg-muted"
                  >
                    <input
                      type="checkbox"
                      checked={selectedFilters.includes(sourceType)}
                      onChange={() => toggleFilter(sourceType)}
                      className="w-3 h-3 rounded"
                    />
                    {sourceType === "reddit" && (
                      <FontAwesomeIcon icon={faRedditAlien} className="w-3 h-3 text-orange-500" />
                    )}
                    {sourceType === "twitter" && (
                      <FontAwesomeIcon icon={faXTwitter} className="w-3 h-3 text-white" />
                    )}
                    {sourceType === "news" && (
                      <Newspaper className="w-3 h-3 text-green-500" />
                    )}
                    <span className="capitalize">{sourceType}</span>
                    <span className="text-gray-400">({count})</span>
                  </label>
                )
              )}
            </div>
          )}
        </div>
      </div>

      {/* Sources Display */}
      <div
        className={cn(
          "overflow-hidden min-w-0 relative",
          contentView === "preview" && "max-h-[90vh]"
        )}
      >
        <div ref={contentRefCallback} className="p-2">
          <div className="grid" style={{ gridTemplateColumns: 'minmax(0, 1fr)' }}>
            <div ref={sourcesContainerRef}>
              {filteredSources.length > 0 ? (
                <SourceDisplay
                  sources={filteredSources}
                  highlightedSourceIndex={highlightedSourceIndex}
                />
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  {sourcesWithOriginalIndex.length > 0
                    ? "No sources match the current filters"
                    : "No sources available"}
                </div>
              )}
            </div>
          </div>
        </div>
        {contentView === "preview" && isContentOverflowing && (
          <div className="absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-t from-background via-background/80 to-transparent pointer-events-none" />
        )}
      </div>
      {contentView === "preview" && isContentOverflowing && (
        <div className="px-3 py-2 flex justify-center">
          <button
            onClick={() => setContentView("expanded")}
            className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
          >
            Show More <ChevronDown className="w-4 h-4" />
          </button>
        </div>
      )}
      {contentView === "expanded" && (
        <div className="px-3 py-2 flex justify-center">
          <button
            onClick={() => setContentView("preview")}
            className="text-xs text-foreground bg-secondary hover:bg-secondary/80 border border-border rounded-md px-2 py-1 transition-all duration-200 flex items-center gap-1 shadow-sm"
          >
            Show Less <ChevronUp className="w-4 h-4" />
          </button>
        </div>
      )}
    </motion.div>
  );
}

// Memoize with selective comparison
export const SourcesTab = memo(SourcesTabComponent, (prevProps, nextProps) => {
  return (
    equal(prevProps.sourcesWithOriginalIndex, nextProps.sourcesWithOriginalIndex) &&
    prevProps.highlightedSourceIndex === nextProps.highlightedSourceIndex &&
    prevProps.sortOrder === nextProps.sortOrder &&
    equal(prevProps.selectedFilters, nextProps.selectedFilters) &&
    prevProps.isFilterOpen === nextProps.isFilterOpen &&
    prevProps.contentView === nextProps.contentView &&
    prevProps.isContentOverflowing === nextProps.isContentOverflowing
  );
});
